import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { IoMdSearch } from "react-icons/io";
import { PiMapPinLineFill } from "react-icons/pi";
import * as yup from "yup";
import { getCleanedAddressFromAutoComplete } from "../../../helpers/helpers";
import GoogleMap from "../../../shared/map";
import MapLabeledInput from "../../../shared/map/MapLabeledInput";
import P from "../../../shared/typography/P";
import Button from "../../../shared/ui/Button";
import FormikLabeledInput from "../../../shared/ui/Form/FormikLabeledInput";
import { addressCityStatePinCodeSchemaShape } from "../../../shared/ui/Form/schema";

const center = {
    lat: 18.5204303,
    lng: 73.8567437,
};
const ProjectForm = ({
    exitingValues,
    onSubmit,
    currentStep,
    setCurrentStep,
}) => {
    const siteRef = useRef();
    const [initialValues, setInitialValues] = useState({
        lat: exitingValues?.latitude || center.lat,
        lng: exitingValues?.longitude || center.lng,
        address: exitingValues?.address || "",
        city: exitingValues?.city || "",
        state: exitingValues?.state || "",
        pincode: exitingValues?.pincode,
        projectName: exitingValues?.projectName,
    });

    const [position, setPosition] = useState(center);

    useEffect(() => {
        if (initialValues.lat && initialValues.lng) {
            setPosition({
                lat: Number(initialValues.lat),
                lng: Number(initialValues.lng),
            });
        }
    }, [initialValues.address, initialValues.lat, initialValues.lng]);

    useEffect(() => {
        if (siteRef.current) {
            siteRef.current.value = exitingValues.address;
        }
    }, [exitingValues.address]);

    const handlePlaceChange = async (place, siteRef, setFieldValue) => {
        const suggestion = siteRef;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setPosition({ lat, lng });

        const { city, state, cleanedAddress, pincode } =
            await getCleanedAddressFromAutoComplete(
                place,
                suggestion,
                lat,
                lng
            );

        setFieldValue("address", cleanedAddress);
        setFieldValue("city", city);
        setFieldValue("state", state);
        setFieldValue("pincode", pincode);
        setFieldValue("lat", lat);
        setFieldValue("lng", lng);
    };

    const handleSubmit = (values, actions) => {
        setInitialValues(values);
        if (currentStep === 1) {
            setCurrentStep(2);
        } else {
            onSubmit(values);
        }
    };

    const getValidationSchema = () => {
        if (currentStep === 1)
            return yup.object().shape({
                address: yup.string().required().label("Address"),
                projectName: yup
                    .string()
                    .required()
                    .min(3)
                    .max(50)
                    .label("Project Name"),
            });
        else {
            return yup
                .object()
                .shape({ ...addressCityStatePinCodeSchemaShape });
        }
    };

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={getValidationSchema()}
            onSubmit={handleSubmit}
        >
            {({
                values,

                setFieldValue,

                isValid,

                dirty,
            }) => (
                <Form className="space-y-2 p-3">
                    <div className="grid grid-cols-1 mt-1 gap-2">
                        {currentStep === 1 ? (
                            <>
                                <FormikLabeledInput
                                    placeholder="Enter Project Name"
                                    label="Project Name"
                                    required
                                    name="projectName"
                                />

                                <GoogleMap
                                    position={position}
                                    setPosition={setPosition}
                                    onPlaceSelect={(place) => {
                                        handlePlaceChange(
                                            place,
                                            siteRef?.current?.value,
                                            setFieldValue
                                        );
                                    }}
                                    className={"mb-2"}
                                    autocompleteInput={
                                        <MapLabeledInput
                                            placeholder="Enter Your Location"
                                            label="Search Address"
                                            name="address"
                                            required
                                            ref={siteRef}
                                            icon={<IoMdSearch />}
                                            iconWrapperClass="text-primary"
                                        />
                                    }
                                />
                                {values.address &&
                                    values.projectName &&
                                    isValid &&
                                    dirty && (
                                        <div className="mt-2">
                                            <div className="flex items-center gap-1">
                                                <PiMapPinLineFill
                                                    size={26}
                                                    className="text-orange"
                                                />
                                                <P className="text-sm text-orange font-vietnam">
                                                    {values.projectName}
                                                </P>
                                            </div>

                                            <P className="text-xs font-vietnam">
                                                {values.address}
                                            </P>
                                        </div>
                                    )}
                            </>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 gap-1.6 mt-1.6 lg:gap-x-[3rem] xl:gap-x-3.2 xl:mt-3.6">
                                    <GoogleMap
                                        position={position}
                                        setPosition={setPosition}
                                    />
                                </div>
                                <p className=" text-xs">
                                    Update the address if not showing correctly
                                </p>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-3 mt-2">
                                    <FormikLabeledInput
                                        label="Address"
                                        name="address"
                                        placeholder="Address"
                                        required
                                    />
                                    <FormikLabeledInput
                                        label="City"
                                        name="city"
                                        placeholder="City"
                                        required
                                    />
                                    <FormikLabeledInput
                                        label="State"
                                        name="state"
                                        placeholder="State"
                                        required
                                    />
                                    <FormikLabeledInput
                                        label="Pincode"
                                        name="pincode"
                                        placeholder="Pincode"
                                        required
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <Button
                        type="submit"
                        className="w-full"
                        disabled={!isValid}
                    >
                        {currentStep === 1
                            ? "Confirm Location"
                            : "Submit Details"}
                    </Button>
                </Form>
            )}
        </Formik>
    );
};

export default ProjectForm;
